import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  Sparkles, LayoutDashboard, Package, Award, MessageSquare,
  Image, Building2, FileText, Mail, Inbox, LogOut, User, Palette,
  MapPin, Scale, Calculator, Star, Menu, X, AtSign, UserCog
} from 'lucide-react';
import { adminGetStats } from '../api/api';

export const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasPerm, roleLabel } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [enAttente, setEnAttente] = useState({ quotes_new: 0, messages_new: 0 });

  // Pastilles de notification : rafraîchies à chaque changement de page et
  // toutes les deux minutes, pour voir arriver une demande sans recharger.
  useEffect(() => {
    let actif = true;
    const rafraichir = () =>
      adminGetStats()
        .then((s) => actif && setEnAttente({ quotes_new: s.quotes_new, messages_new: s.messages_new }))
        .catch(() => {});

    rafraichir();
    const intervalle = setInterval(rafraichir, 120000);
    return () => {
      actif = false;
      clearInterval(intervalle);
    };
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  // Accès par permission (voir ROLE_PERMISSIONS côté backend). Sans clé `perm`,
  // l'entrée est visible par tous. Chaque séparateur porte la permission des
  // entrées qu'il coiffe : un simple filtre suffit, aucun titre ne reste orphelin.
  const menuItems = [
    { icon: LayoutDashboard, label: 'Tableau de bord', path: '/admin/dashboard', perm: 'dashboard' },
    { icon: Inbox, label: 'Demandes de devis', path: '/admin/quotes', badge: enAttente.quotes_new, perm: 'quotes' },
    { icon: Mail, label: 'Messages', path: '/admin/messages', badge: enAttente.messages_new, perm: 'messages' },
    { icon: AtSign, label: 'Courriel', path: '/admin/courriel', perm: 'courriel' },
    { icon: Star, label: 'Avis clients', path: '/admin/reviews', perm: 'reviews' },
    { separateur: 'Contenu du site', perm: 'content' },
    { icon: Package, label: 'Services', path: '/admin/services', perm: 'content' },
    { icon: Award, label: 'Avantages', path: '/admin/benefits', perm: 'content' },
    { icon: MessageSquare, label: 'Témoignages', path: '/admin/testimonials', perm: 'content' },
    { icon: Image, label: 'Réalisations', path: '/admin/projects', perm: 'content' },
    { icon: MapPin, label: 'Zones desservies', path: '/admin/areas', perm: 'content' },
    { icon: FileText, label: 'À propos', path: '/admin/about', perm: 'content' },
    { separateur: 'Réglages', perm: 'settings' },
    { icon: Building2, label: 'Entreprise', path: '/admin/company', perm: 'settings' },
    { icon: Calculator, label: 'Estimateur', path: '/admin/pricing', perm: 'settings' },
    { icon: Palette, label: 'Apparence', path: '/admin/appearance', perm: 'settings' },
    { icon: Scale, label: 'Pages légales', path: '/admin/legal', perm: 'settings' },
    { separateur: 'Administration', perm: 'users' },
    { icon: UserCog, label: 'Utilisateurs', path: '/admin/users', perm: 'users' },
  ];

  const menuAffiche = menuItems.filter((item) => !item.perm || hasPerm(item.perm));

  const handleNavigation = (path) => {
    navigate(path);
    setSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-gray-900 text-white z-50 px-4 py-3 flex items-center justify-between shadow-lg">
        <div className="flex items-center space-x-3">
          <div className="bg-blue-600 p-1.5 rounded-lg">
            <Sparkles className="w-5 h-5" />
          </div>
          <h1 className="text-lg font-bold">NETTO+ Admin</h1>
        </div>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-lg hover:bg-gray-800 transition-colors"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed left-0 top-0 h-full w-64 bg-gradient-to-b from-gray-900 to-gray-800 text-white shadow-lg z-50
        flex flex-col
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Logo - Hidden on mobile (shown in header) */}
        <div className="hidden lg:block p-6 border-b border-gray-700 shrink-0">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-600 p-2 rounded-lg">
              <Sparkles className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold">NETTO+ Admin</h1>
              <p className="text-sm text-gray-400 truncate max-w-[140px]">{user?.email}</p>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-400">
                {roleLabel}
              </p>
            </div>
          </div>
        </div>

        {/* Mobile spacer for header */}
        <div className="lg:hidden h-14" />

        {/* Navigation */}
        <nav className="p-4 space-y-1 overflow-y-auto flex-1 min-h-0">
          {menuAffiche.map((item, index) => {
            if (item.separateur) {
              return (
                <p
                  key={`sep-${index}`}
                  className="px-4 pt-5 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500"
                >
                  {item.separateur}
                </p>
              );
            }

            const Icon = item.icon;
            const isActive = location.pathname === item.path;

            return (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                <span className="font-medium text-sm flex-1 text-left">{item.label}</span>
                {item.badge > 0 && (
                  <span
                    className={`min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold flex items-center justify-center ${
                      isActive ? 'bg-white text-blue-600' : 'bg-blue-600 text-white'
                    }`}
                    aria-label={`${item.badge} en attente`}
                  >
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="shrink-0 p-4 border-t border-gray-700 space-y-2 bg-gray-900">
          <button
            onClick={() => handleNavigation('/admin/profile')}
            className="w-full flex items-center space-x-3 px-4 py-2 rounded-lg text-gray-300 hover:bg-gray-700 hover:text-white transition-all duration-200"
          >
            <User className="w-4 h-4" />
            <span className="font-medium text-sm">Mon profil</span>
          </button>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full bg-transparent border-gray-600 text-gray-300 hover:bg-red-600 hover:text-white hover:border-red-600"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Déconnexion
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-14 lg:pt-0">
        {/* Top Bar */}
        <div className="bg-white border-b border-gray-200 px-4 lg:px-8 py-4 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="text-xl lg:text-2xl font-bold text-gray-900">
              {menuItems.find((item) => item.path === location.pathname)?.label || 'Administration'}
            </h2>
            <button
              onClick={() => navigate('/')}
              className="text-blue-600 hover:text-blue-700 font-medium text-sm"
            >
              Voir le site →
            </button>
          </div>
        </div>

        {/* Page Content */}
        <div className="p-4 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};
